import { io, Socket } from 'socket.io-client';
import store2 from 'store2';
import { useVehicleStore, VehicleStatus } from '../store/vehicle';

// WebSocket config: empty string = same origin (for production behind nginx proxy)
const WS_BASE_URL = import.meta.env.VITE_WS_BASE_URL || '';
const WS_NAMESPACE = '/jiushi';

// Socket event types — aligned with backend JiushiGateway
export enum SocketEvent {
    // Connection events
    CONNECT = 'connect',
    DISCONNECT = 'disconnect',
    CONNECT_ERROR = 'connect_error',

    // Subscribe events (backend: @SubscribeMessage('subscribe') / 'unsubscribe')
    SUBSCRIBE = 'subscribe',
    UNSUBSCRIBE = 'unsubscribe',

    // Data events (backend emits: vehicle:realtime / vehicle:business)
    VEHICLE_REALTIME = 'vehicle:realtime',
    VEHICLE_BUSINESS = 'vehicle:business',
}

// Message types — aligned with backend VehicleRealtimePayload / VehicleBusinessPayload
export interface VehicleRealtimeMessage {
    vehicleName: string;
    lon: number;
    lat: number;
    heading: number;
    speed: number;
    power: number;
    timestamp: number;
    [key: string]: any;
}

export interface VehicleBusinessMessage {
    vehicleName: string;
    vehicleBusinessStatus: string;
    vehicleBusinessStatusName?: string;
    currentStopName?: string;
    lastStopName?: string;
    timestamp: number;
    [key: string]: any;
}

class SocketService {
    private socket: Socket | null = null;
    private maxReconnectAttempts = 5;
    private reconnectInterval = 3000;
    private subscribedVehicles: Set<string> = new Set();

    // Event callbacks
    private onConnectCallback?: () => void;
    private onDisconnectCallback?: (reason: string) => void;
    private onErrorCallback?: (error: Error) => void;

    connect(): void {
        if (this.socket?.connected) {
            console.log('[Socket] Already connected');
            return;
        }

        const token = store2.get('token');

        this.socket = io(`${WS_BASE_URL}${WS_NAMESPACE}`, {
            auth: { token },
            transports: ['websocket', 'polling'],
            reconnection: true,
            reconnectionAttempts: this.maxReconnectAttempts,
            reconnectionDelay: this.reconnectInterval,
        });

        this.setupEventListeners();
    }

    disconnect(): void {
        if (this.socket) {
            this.socket.disconnect();
            this.socket = null;
        }
        this.subscribedVehicles.clear();
    }

    private setupEventListeners(): void {
        if (!this.socket) return;

        this.socket.on(SocketEvent.CONNECT, () => {
            console.log('[Socket] Connected');

            // Re-subscribe on reconnect
            if (this.subscribedVehicles.size > 0) {
                this.socket?.emit(SocketEvent.SUBSCRIBE, {
                    vehicleNames: Array.from(this.subscribedVehicles),
                });
            }

            this.onConnectCallback?.();
        });

        this.socket.on(SocketEvent.DISCONNECT, (reason: string) => {
            console.log('[Socket] Disconnected:', reason);
            this.onDisconnectCallback?.(reason);
        });

        this.socket.on(SocketEvent.CONNECT_ERROR, (error: Error) => {
            console.error('[Socket] Connection error:', error);
            this.onErrorCallback?.(error);
        });

        // vehicle:realtime — position + battery (power)
        this.socket.on(SocketEvent.VEHICLE_REALTIME, (message: VehicleRealtimeMessage) => {
            const store = useVehicleStore.getState();
            store.updatePosition(message.vehicleName, {
                lon: message.lon,
                lat: message.lat,
                heading: message.heading,
                speed: message.speed,
                timestamp: message.timestamp,
            });
            // Update battery from realtime power field
            if (message.power !== undefined) {
                const currentStatus = store.getStatus(message.vehicleName);
                store.updateStatus(message.vehicleName, {
                    status: (currentStatus?.status || 'ONLINE') as VehicleStatus['status'],
                    battery: message.power,
                    lastUpdate: message.timestamp,
                });
            }
        });

        // vehicle:business — status changes
        this.socket.on(SocketEvent.VEHICLE_BUSINESS, (message: VehicleBusinessMessage) => {
            const store = useVehicleStore.getState();
            const currentStatus = store.getStatus(message.vehicleName);
            store.updateStatus(message.vehicleName, {
                status: message.vehicleBusinessStatus as VehicleStatus['status'],
                battery: currentStatus?.battery || 0,
                lastUpdate: message.timestamp,
            });
        });
    }

    /**
     * Subscribe to specific vehicles (backend expects { vehicleNames: string[] })
     */
    subscribeVehicles(vehicleNames: string[]): void {
        if (!this.socket?.connected) {
            console.warn('[Socket] Not connected, cannot subscribe');
            return;
        }
        for (const name of vehicleNames) {
            this.subscribedVehicles.add(name);
        }
        this.socket.emit(SocketEvent.SUBSCRIBE, { vehicleNames });
        console.log('[Socket] Subscribed to vehicles:', vehicleNames.length);
    }

    /**
     * Unsubscribe from specific vehicles
     */
    unsubscribeVehicles(vehicleNames: string[]): void {
        if (!this.socket?.connected) return;
        for (const name of vehicleNames) {
            this.subscribedVehicles.delete(name);
        }
        this.socket.emit(SocketEvent.UNSUBSCRIBE, { vehicleNames });
    }

    /**
     * Subscribe to a single vehicle
     */
    subscribeVehicle(vehicleName: string): void {
        this.subscribeVehicles([vehicleName]);
    }

    /**
     * Unsubscribe from a single vehicle
     */
    unsubscribeVehicle(vehicleName: string): void {
        this.unsubscribeVehicles([vehicleName]);
    }

    isConnected(): boolean {
        return this.socket?.connected ?? false;
    }

    onConnect(callback: () => void): void {
        this.onConnectCallback = callback;
    }

    onDisconnect(callback: (reason: string) => void): void {
        this.onDisconnectCallback = callback;
    }

    onError(callback: (error: Error) => void): void {
        this.onErrorCallback = callback;
    }
}

// 导出单例
export const socketService = new SocketService();
export default socketService;
